import React, { useEffect, useState } from 'react';

import { AnalysisSettings, LoadSettings } from '@paragon/contract-library';
import { getInsertionsWithPendingChanges, getLayoutModelsWithPendingChanges } from '@paragon/layout-models';
import { ReadonlyDictionary } from '@paragon/utilities';

import { RootState } from '../../../store';
import { loadAnalysisSettingsBulk, loadLoadSettingsBulk } from '../../../store/actions/loadActions';
import { useAppDispatch, useAppSelector } from '../../hooks';
import { LoadedProjectWideSettingsDialog } from './LoadedProjectWideSettingsDialog';
import { LoadingSettingsDialog } from './LoadingSettingsDialog';
import { getAllDescendantsOfLayoutModel } from './ProjectHierarchyCheckboxTree';
import { SettingsErrorDialog } from './SettingsErrorDialog';
import { DataWithStatus, useFetchedPresets } from './useFetchedPresets';

function getProjectInProjectContext(state: RootState) {
  const { openContext } = state.application;
  if (openContext?.type !== 'project')
    throw new Error('The project-wide settings dialog can only be used in the context of a project');
  const project = state.layoutModels[openContext.projectGuid];
  if (!project) throw new Error('The project could not be found.');
  return project;
}

export type SettingsDialogTab = 'analysisSettings' | 'loadSettings';

export interface ProjectWideSettingsDialogProps {
  readonly initialTab: SettingsDialogTab;
  readonly initiallySelectedComponentDesignGuids: readonly string[];
}

type SettingsState<T> = DataWithStatus<ReadonlyDictionary<T>>;

export function ProjectWideSettingsDialog(props: ProjectWideSettingsDialogProps) {
  const dispatch = useAppDispatch();

  const [loadSettingsByModelGuid, setLoadSettingsByModelGuid] = useState<SettingsState<LoadSettings>>('fetching');
  const [analysisSettingsByModelGuid, setAnalysisSettingsByModelGuid] =
    useState<SettingsState<AnalysisSettings>>('fetching');

  const { analysisSettingsPresets, loadSettingsPresets, invalidate } = useFetchedPresets([
    'loadSettings',
    'analysisSettings',
  ]);

  const project = useAppSelector(getProjectInProjectContext);

  const layoutModels = useAppSelector(getLayoutModelsWithPendingChanges);
  const insertions = useAppSelector(getInsertionsWithPendingChanges);
  const layoutModelGuidsForOpenProject = Array.from(getAllDescendantsOfLayoutModel(project, layoutModels, insertions));

  useEffect(() => {
    dispatch(loadLoadSettingsBulk(layoutModelGuidsForOpenProject))
      .then(setLoadSettingsByModelGuid)
      .catch(() => setLoadSettingsByModelGuid('error'));

    dispatch(loadAnalysisSettingsBulk(layoutModelGuidsForOpenProject))
      .then(setAnalysisSettingsByModelGuid)
      .catch(() => setAnalysisSettingsByModelGuid('error'));

    /* eslint-disable-next-line react-hooks/exhaustive-deps --
     * We only want this to run once on mount, since we don't want to stomp on any changes made in state. We do not
     * expect any changes to the existence of layout models while this dialog is open.
     **/
  }, []);

  const [fastFetchTimeComplete, setFastFetchTimeComplete] = useState(false);
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setFastFetchTimeComplete(true);
    }, 300);

    return () => clearTimeout(timeoutId);
  }, []);

  if (
    loadSettingsByModelGuid === 'error' ||
    analysisSettingsByModelGuid === 'error' ||
    loadSettingsPresets === 'error' ||
    analysisSettingsPresets === 'error'
  ) {
    return (
      <SettingsErrorDialog>
        There was an error loading the settings for this project. Try closing and re-opening the dialog, or contact
        Paragon Support if the error persists.
      </SettingsErrorDialog>
    );
  }

  if (
    loadSettingsByModelGuid === 'fetching' ||
    analysisSettingsByModelGuid === 'fetching' ||
    loadSettingsPresets === 'fetching' ||
    analysisSettingsPresets === 'fetching'
  ) {
    // Avoid displaying a spinner for a split second
    return fastFetchTimeComplete ? <LoadingSettingsDialog /> : null;
  }

  return (
    <LoadedProjectWideSettingsDialog
      project={project}
      initialTab={props.initialTab}
      initiallySelectedComponentDesignGuids={
        props.initiallySelectedComponentDesignGuids ?? layoutModelGuidsForOpenProject
      }
      analysisSettingsPresets={analysisSettingsPresets}
      loadSettingsPresets={loadSettingsPresets}
      loadSettingsByModelGuid={loadSettingsByModelGuid}
      analysisSettingsByModelGuid={analysisSettingsByModelGuid}
      onLoadSettingsUpdated={(callback) => {
        setLoadSettingsByModelGuid((state) => {
          if (state && typeof state === 'object') return callback(state);
          throw new Error('Invalid load settings state -- updated when null or in a special state.');
        });
      }}
      onAnalysisSettingsUpdated={(callback) => {
        setAnalysisSettingsByModelGuid((state) => {
          if (state && typeof state === 'object') return callback(state);
          throw new Error('Invalid analysis settings state -- updated when null or in a special state.');
        });
      }}
      onPresetsInvalidated={(options) =>
        invalidate(['loadSettings', 'analysisSettings'], { suppressFetchingState: options?.suppressSpinner })
      }
    />
  );
}
